package com.eladfinish.bible.contest.features.verseWidget

import com.eladfinish.bible.contest.analytics.AnalyticsConstants
import com.eladfinish.bible.contest.analytics.providers.AnalyticsProvider
import com.russhwolf.settings.Settings
import kotlin.time.Clock
import kotlin.time.Instant

/** How a widget came to be on a home screen. */
enum class VerseWidgetAddedVia(val analyticsValue: String) {
    PIN_DIALOG(AnalyticsConstants.VerseWidget.VIA_PIN_DIALOG),
    WIDGET_PICKER(AnalyticsConstants.VerseWidget.VIA_WIDGET_PICKER),
    DETECTED_ON_OPEN(AnalyticsConstants.VerseWidget.VIA_DETECTED_ON_OPEN),
}

/** What on the widget was tapped to open the app. */
enum class VerseWidgetTapTarget(val analyticsValue: String) {
    VERSE(AnalyticsConstants.VerseWidget.TARGET_VERSE),
    GEAR(AnalyticsConstants.VerseWidget.TARGET_GEAR),
}

/**
 * The widget's own events: added, removed, tapped and refreshed, each with the settings the widget had
 * ([VerseWidgetAnalytics.params]), and the user properties that follow it being added or removed. The widget
 * runs no analytics code of its own, so the platforms call this from the places that learn of it (the provider,
 * the app opened from the widget).
 */
class VerseWidgetEventsReporter(
    private val settings: Settings,
    private val configRepository: VerseWidgetConfigRepository,
    private val analyticsProvider: AnalyticsProvider,
    private val updateUserProperty: UpdateVerseWidgetUserPropertyUseCase,
    private val now: () -> Instant = { Clock.System.now() },
) {

    /**
     * A widget was added. Adding one through the system's pin dialog is also seen by the provider as its
     * first widget, so a second report within [DUPLICATE_WINDOW_SECONDS] is the same widget and is dropped.
     */
    fun onAdded(via: VerseWidgetAddedVia) {
        val seconds = now().epochSeconds
        val last = settings.getLong(KEY_LAST_ADDED, 0L)
        if (seconds - last in 0..DUPLICATE_WINDOW_SECONDS) return

        settings.putLong(KEY_LAST_ADDED, seconds)
        if (!settings.hasKey(KEY_FIRST_ADDED)) settings.putLong(KEY_FIRST_ADDED, seconds)
        analyticsProvider.logEvent(
            AnalyticsConstants.VerseWidget.EVENT_ADDED,
            VerseWidgetAnalytics.params(configRepository.get()) +
                    (AnalyticsConstants.VerseWidget.PARAM_VIA to via.analyticsValue),
        )
        updateUserProperty()
    }

    /** The last widget was removed: how long it was kept goes with the event. */
    fun onRemoved() {
        val firstAdded = settings.getLong(KEY_FIRST_ADDED, now().epochSeconds)
        val ageDays = ((now().epochSeconds - firstAdded) / SECONDS_PER_DAY).coerceAtLeast(0)
        settings.remove(KEY_FIRST_ADDED)
        settings.remove(KEY_LAST_ADDED)
        analyticsProvider.logEvent(
            AnalyticsConstants.VerseWidget.EVENT_REMOVED,
            VerseWidgetAnalytics.params(configRepository.get()) +
                    (AnalyticsConstants.VerseWidget.PARAM_AGE_DAYS to ageDays),
        )
        updateUserProperty()
    }

    /** The app was opened from the widget. */
    fun onTap(target: VerseWidgetTapTarget) {
        analyticsProvider.logEvent(
            AnalyticsConstants.VerseWidget.EVENT_TAP,
            VerseWidgetAnalytics.params(configRepository.get()) +
                    (AnalyticsConstants.VerseWidget.PARAM_TARGET to target.analyticsValue),
        )
    }

    /** The user pressed the widget's button for a new verse. */
    fun onRefresh() {
        analyticsProvider.logEvent(
            AnalyticsConstants.VerseWidget.EVENT_REFRESH,
            VerseWidgetAnalytics.params(configRepository.get()),
        )
    }

    /**
     * The iOS widget's button cannot report its presses (the extension runs no analytics): it counts them in a file, and
     * the app, when it is opened, passes the count here. Reports one [onRefresh] for each press since the count last seen,
     * at most [MAX_REFRESHES_PER_OPEN] at a time. A count that went down is a file that was started again.
     */
    fun onRefreshesSeen(total: Int) {
        val seen = settings.getInt(KEY_REFRESHES_SEEN, 0)
        if (total == seen) return
        settings.putInt(KEY_REFRESHES_SEEN, total)
        val presses = if (total > seen) total - seen else total
        repeat(presses.coerceAtMost(MAX_REFRESHES_PER_OPEN)) { onRefresh() }
    }

    private companion object {
        const val KEY_REFRESHES_SEEN = "verse_widget_refreshes_seen"
        const val MAX_REFRESHES_PER_OPEN = 20
        const val KEY_FIRST_ADDED = "verse_widget_first_added"
        const val KEY_LAST_ADDED = "verse_widget_last_added"
        const val DUPLICATE_WINDOW_SECONDS = 120L
        const val SECONDS_PER_DAY = 86_400L
    }
}
